import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Backdrop, CircularProgress, Box } from '@mui/material';

interface LoadingContextType {
  showLoading: () => void;
  stopLoading: () => void;
  isLoading: boolean;
}

interface LoadingProviderProps {
  children: ReactNode;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

const styles = {
  backdrop: {
    color: '#2196F3',
    zIndex: (theme: any) => theme.zIndex.drawer + 1,
    backgroundColor: 'transparent'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2
  },
  spinner: {
    color: '#2196F3'
  },
  spinnerSize: 60
};

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  const showLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  try {
    return (
      <LoadingContext.Provider value={{
        showLoading,
        stopLoading,
        isLoading
      }}>
        {children}
        
        {/* Full Page Loading Spinner */}
        <Backdrop
          sx={styles.backdrop}
          open={isLoading}
        >
          <Box sx={styles.container}>
            <CircularProgress 
              size={styles.spinnerSize} 
              sx={styles.spinner}
            />
          </Box>
        </Backdrop>
      </LoadingContext.Provider>
    );
  } catch (error) {
    // If loading provider fails, just render children without loading functionality
    console.error('LoadingProvider render error:', error);
    return <>{children}</>;
  }
};

// Custom hook to use loading
export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within LoadingProvider');
  }
  return context;
};